<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import {
  iButton,
  iModal,
  iSpinner,
  iTextInput,
  iToggle,
  IToggleSizes,
  openModal,
} from '@inscribe/signal';
import DecisionEngineCard from '@/V2/decision-engine/DecisionEngineCard.vue';
import EmptyPage from '@/V2/components/EmptyPage';
import RuleDelete from '@/V2/decision-engine/rules/RuleDelete.vue';
import RuleSave from '@/V2/decision-engine/rules/RuleSave.vue';
import RuleEngine from '@/utils/ruleEngine';

export default {
  components: {
    DecisionEngineCard,
    EmptyPage,
    iButton,
    iModal,
    iSpinner,
    iTextInput,
    iToggle,
    RuleDelete,
    RuleSave,
  },
  data() {
    return {
      displayLink:
        'https://help.inscribe.ai/en/articles/6173054-understanding-the-decision-engine',
      IToggleSizes,
      RULE_TYPES: {
        TYPE_DEFAULT: 'default',
        TYPE_CUSTOM: 'custom',
      },
      selectedRuleType: null,
      newRuleName: '',
      ruleToBeDeleted: null,
      rulesToBeSaved: null,
      nextRuleCount: 1,
      isLoading: true,
    };
  },
  computed: {
    ...mapGetters(['getRules', 'getRulesSchema', 'isOrgAdmin']),
    validRuleName() {
      return this.newRuleName.trim() !== '';
    },
    visibleRules() {
      return this.getRules.filter(
        (r) =>
          (this.selectedRuleType === this.RULE_TYPES.TYPE_DEFAULT &&
            r.default &&
            !RuleEngine.isSimpleDetectorRule(r)) ||
          (this.selectedRuleType === this.RULE_TYPES.TYPE_CUSTOM && !r.default),
      );
    },
  },
  mounted() {
    this.selectedRuleType =
      this.$route?.query?.section === this.RULE_TYPES.TYPE_CUSTOM
        ? this.RULE_TYPES.TYPE_CUSTOM
        : this.RULE_TYPES.TYPE_DEFAULT;

    if (this.isOrgAdmin) {
      this.getConfigRules().then(() => {
        this.isLoading = false;
      });
    }
  },
  methods: {
    ...mapActions(['getConfigRules']),
    ...mapGetters(['getRuleIndex']),
    ...mapMutations(['setRules', 'setCurrentEditingItem']),
    onTypeToggle(value) {
      this.selectedRuleType = value
        ? this.RULE_TYPES.TYPE_DEFAULT
        : this.RULE_TYPES.TYPE_CUSTOM;
    },
    addNewRule() {
      openModal('new-rule-modal');
    },
    createRule() {
      const name = this.newRuleName.trim();
      const newRule = {
        name: name,
        display_name: name,
        actions: [{ name: 'fraudulent' }],
        enabled: true,
        default: false,
        isDraft: true,
        conditions: {
          all: [
            {
              name: 'detector_results',
              operator: 'shares_at_least_one_element_with',
              value: null,
            },
          ],
        },
      };

      this.setCurrentEditingItem(newRule);
      this.$router.push({
        name: 'decision-engine-rule-edit',
      });
    },
    getRuleTitle(rule) {
      return RuleEngine.getRuleTitle(rule);
    },
    getRuleSubtitle(rule) {
      const conditionsCount = rule.conditions?.all?.length || 0;
      return `${conditionsCount} ${
        conditionsCount === 1 ? 'condition' : 'conditions'
      } • ${rule.enabled ? 'Enabled' : 'Disabled'}`;
    },
    onDeleteClick(params) {
      this.ruleToBeDeleted = params.item;
    },
    onEditClick(params) {
      this.$emit('hide-tooltips');
      this.setCurrentEditingItem(params.item);
      this.$router.push({
        name: 'decision-engine-rule-edit',
      });
    },
    onSwitchChange(params) {
      this.$emit('hide-tooltips');
      const foundRuleIdx = this.getRuleIndex()(params.item);
      if (foundRuleIdx > -1) {
        this.rulesToBeSaved = [
          ...this.getRules.slice(0, foundRuleIdx),
          { ...params.item, enabled: params.value },
          ...this.getRules.slice(foundRuleIdx + 1),
        ];
      }
    },
    onRuleNameChange(value) {
      this.newRuleName = value;
    },
    deleteRule(newRules) {
      this.rulesToBeSaved = newRules;
    },
  },
};
</script>

<template>
  <div class="rules-page d-flex flex-column i-gap-12">
    <div class="d-flex justify-content-between align-items-center">
      <div class="text flex-grow-1">
        <div class="i-text-heading-5 i-text-neutral-120 i-mb-2">
          Fraud Rules
        </div>
        <div class="i-text-body-2 i-text-neutral-110 subheading-wrap">
          Manage fraud rules for this workspace.
          <a rel="noopener" class="i-link" :href="displayLink" target="_blank"
            >Learn more</a
          >
        </div>
      </div>
      <div v-if="getRules.length > 0" class="flex-shrink-0">
        <i-button
          v-if="isOrgAdmin"
          text="New Fraud Rule"
          data-testid="new-rule-button"
          size="md"
          @button-click="addNewRule()"
        ></i-button>
      </div>
    </div>

    <!-- type selection -->
    <i-toggle
      class="type-toggle"
      :toggle-value="selectedRuleType === RULE_TYPES.TYPE_CUSTOM ? false : true"
      left-label="Default"
      right-label="Custom"
      :full-width="true"
      :size="IToggleSizes.SMALL"
      data-testid="rule-type-toggle"
      @value-update="onTypeToggle"
    />

    <!-- loading screen-->
    <div
      v-if="isLoading"
      class="d-flex i-my-5 w-100 align-items-center justify-content-center"
    >
      <i-spinner></i-spinner>
    </div>

    <!-- empty page -->
    <empty-page
      v-if="visibleRules.length === 0 && !isLoading"
      class="d-flex flex-grow-1"
      icon="large-file-settings"
      title="Start by creating a custom fraud rule"
      subtitle="All of your custom fraud rules will appear here."
      :button-text="isOrgAdmin ? 'New Fraud Rule' : null"
      @on-button-click="addNewRule"
    ></empty-page>

    <!-- rules list -->
    <div
      v-if="visibleRules.length > 0 && !isLoading"
      class="rules-list items-list i-radius-2xl"
      data-testid="rules-list"
    >
      <decision-engine-card
        v-for="(rule, ruleIndex) in visibleRules"
        :key="`${ruleIndex}-${JSON.stringify(rule)}`"
        :title="getRuleTitle(rule)"
        :subtitle="getRuleSubtitle(rule)"
        :item="rule"
        icon="medium-file-settings-3"
        tooltip-toggle-on="Disable Rule"
        tooltip-toggle-off="Enable Rule"
        tooltip-edit="Edit Rule"
        tooltip-delete="Delete Rule"
        :display-tools="!rule.default"
        :switch-visible="true"
        :switch-value="rule.enabled"
        @delete-click="onDeleteClick"
        @edit-click="onEditClick"
        @switch-change="onSwitchChange"
        @card-click="onEditClick"
      ></decision-engine-card>
    </div>

    <div class="i-mp-2">&nbsp;</div>

    <!-- rule delete -->
    <rule-delete
      :rule-to-be-deleted="ruleToBeDeleted"
      @on-close="ruleToBeDeleted = null"
      @on-delete="deleteRule"
    ></rule-delete>

    <!-- rules save -->
    <rule-save :new-rules-collection="rulesToBeSaved"></rule-save>

    <!-- new Rule Modal -->
    <i-modal
      modal-id="new-rule-modal"
      data-testid="new-rule-modal"
      title="New Fraud Rule"
      :confirm-button-disabled="!validRuleName"
      confirm-button-text="Continue"
      :submit-on-enter="true"
      :input-focus-on-open="true"
      @on-submit="createRule"
    >
      <template #body-content>
        <div>
          <i-text-input
            ref="newRuleInput"
            :value="newRuleName"
            placeholder="Fraud rule name"
            :show-hint-text="true"
            hint-text="For example: ‘PDF Software’"
            data-testid="new-rule-modal-input"
            @input="onRuleNameChange"
          ></i-text-input>
        </div>
      </template>
    </i-modal>
  </div>
</template>

<style lang="scss" scoped>
.rules-page {
  height: 100%;

  .type-toggle {
    width: 292px;
  }
}
</style>
